import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  User,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Globe,
  Github,
  DollarSign,
  Calendar,
  Briefcase,
  GraduationCap,
  Loader2,
  Save,
  ArrowLeft,
  Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function Profile() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState(null);
  const [profile, setProfile] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    location: "",
    linkedin_url: "",
    portfolio_url: "",
    github_url: "",
    desired_salary: "",
    availability: "",
    work_authorization: "",
    requires_sponsorship: false,
    years_experience: "",
    education: "",
    skills: []
  });
  const [skillInput, setSkillInput] = useState("");

  useEffect(() => {
    fetchProfile();
    fetchQuota();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({
        ...response.data,
        skills: response.data.skills || []
      });
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuota = async () => {
    try {
      const response = await axios.get(`${API}/payments/quota`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuota(response.data);
    } catch (error) {
      console.error("Failed to fetch quota:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile saved successfully!");
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSkill = () => {
    if (skillInput.trim() && !profile.skills.includes(skillInput.trim())) {
      setProfile({
        ...profile,
        skills: [...profile.skills, skillInput.trim()]
      });
      setSkillInput("");
    }
  };

  const handleRemoveSkill = (skill) => {
    setProfile({
      ...profile,
      skills: profile.skills.filter(s => s !== skill)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F3F1EC]">
        <Navbar token={token} quota={quota} onQuotaUpdate={setQuota} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="h-8 w-8 animate-spin text-[#A3C3D8]" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F1EC]" data-testid="profile-page">
      <Navbar token={token} quota={quota} onQuotaUpdate={setQuota} />
      
      <div className="max-w-4xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/")}
              className="text-[#555F67]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Autofill Profile
              </h1>
              <p className="text-sm text-[#94A3B8]">
                This information will be used to auto-fill job applications
              </p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
            data-testid="save-profile-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Profile
          </Button>
        </div>

        {/* Autofill Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 flex items-start gap-3">
          <Zap className="h-5 w-5 text-blue-500 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800">Quick Autofill</h3>
            <p className="text-sm text-blue-700">
              Complete your profile once and our Chrome extension will auto-fill application forms on Greenhouse, Lever, Workday, and more.
            </p>
          </div>
        </div>

        {/* Form Sections */}
        <div className="space-y-8">
          {/* Personal Information */}
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <User className="h-5 w-5 text-[#A3C3D8]" />
              Personal Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name" className="text-[#555F67]">First Name</Label>
                <Input
                  id="first_name"
                  value={profile.first_name}
                  onChange={(e) => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="John"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="first-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name" className="text-[#555F67]">Last Name</Label>
                <Input
                  id="last_name"
                  value={profile.last_name}
                  onChange={(e) => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Doe"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="last-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#555F67] flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  placeholder="john@example.com"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-[#555F67] flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profile.phone || ""}
                  onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="phone-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="location" className="text-[#555F67] flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Location
                </Label>
                <Input
                  id="location"
                  value={profile.location || ""}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="San Francisco, CA"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="location-input"
                />
              </div>
            </div>
          </div>

          {/* Online Presence */}
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Globe className="h-5 w-5 text-[#A3C3D8]" />
              Online Presence
            </h2>
            
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="linkedin" className="text-[#555F67] flex items-center gap-2">
                  <Linkedin className="h-4 w-4" />
                  LinkedIn URL
                </Label>
                <Input
                  id="linkedin"
                  value={profile.linkedin_url || ""}
                  onChange={(e) => setProfile({ ...profile, linkedin_url: e.target.value })}
                  placeholder="https://linkedin.com/in/johndoe"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="linkedin-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="portfolio" className="text-[#555F67] flex items-center gap-2">
                  <Globe className="h-4 w-4" />
                  Portfolio / Website
                </Label>
                <Input
                  id="portfolio"
                  value={profile.portfolio_url || ""}
                  onChange={(e) => setProfile({ ...profile, portfolio_url: e.target.value })}
                  placeholder="https://johndoe.com"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="portfolio-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="github" className="text-[#555F67] flex items-center gap-2">
                  <Github className="h-4 w-4" />
                  GitHub URL
                </Label>
                <Input
                  id="github"
                  value={profile.github_url || ""}
                  onChange={(e) => setProfile({ ...profile, github_url: e.target.value })}
                  placeholder="https://github.com/johndoe"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="github-input"
                />
              </div>
            </div>
          </div>

          {/* Work Preferences */}
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Briefcase className="h-5 w-5 text-[#A3C3D8]" />
              Work Preferences
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="salary" className="text-[#555F67] flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Desired Salary
                </Label>
                <Input
                  id="salary"
                  value={profile.desired_salary || ""}
                  onChange={(e) => setProfile({ ...profile, desired_salary: e.target.value })}
                  placeholder="$120,000 - $150,000"
                  className="border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="salary-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="availability" className="text-[#555F67] flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Availability
                </Label>
                <Select 
                  value={profile.availability || ""} 
                  onValueChange={(value) => setProfile({ ...profile, availability: value })}
                >
                  <SelectTrigger data-testid="availability-select">
                    <SelectValue placeholder="Select availability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediately">Immediately</SelectItem>
                    <SelectItem value="2_weeks">2 weeks notice</SelectItem>
                    <SelectItem value="1_month">1 month notice</SelectItem>
                    <SelectItem value="2_months">2+ months notice</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="experience" className="text-[#555F67]">Years of Experience</Label>
                <Select 
                  value={profile.years_experience || ""} 
                  onValueChange={(value) => setProfile({ ...profile, years_experience: value })}
                >
                  <SelectTrigger data-testid="experience-select">
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0-1">0-1 years</SelectItem>
                    <SelectItem value="1-3">1-3 years</SelectItem>
                    <SelectItem value="3-5">3-5 years</SelectItem>
                    <SelectItem value="5-10">5-10 years</SelectItem>
                    <SelectItem value="10+">10+ years</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="authorization" className="text-[#555F67]">Work Authorization</Label>
                <Select 
                  value={profile.work_authorization || ""} 
                  onValueChange={(value) => setProfile({ ...profile, work_authorization: value })}
                >
                  <SelectTrigger data-testid="authorization-select">
                    <SelectValue placeholder="Select authorization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="citizen">US Citizen</SelectItem>
                    <SelectItem value="permanent_resident">Permanent Resident</SelectItem>
                    <SelectItem value="work_visa">Work Visa (H1B, L1, etc.)</SelectItem>
                    <SelectItem value="student_visa">Student Visa (F1, OPT)</SelectItem>
                    <SelectItem value="need_sponsorship">Need Sponsorship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label htmlFor="sponsorship" className="text-[#555F67]">Require Visa Sponsorship?</Label>
                  <p className="text-sm text-[#94A3B8]">Toggle if you need employer sponsorship</p>
                </div>
                <Switch
                  id="sponsorship"
                  checked={profile.requires_sponsorship || false}
                  onCheckedChange={(checked) => setProfile({ ...profile, requires_sponsorship: checked })}
                  data-testid="sponsorship-switch"
                />
              </div>
            </div>
          </div>

          {/* Education & Skills */}
          <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm">
            <h2 className="text-lg font-semibold text-[#2D3748] mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <GraduationCap className="h-5 w-5 text-[#A3C3D8]" />
              Education & Skills
            </h2>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="education" className="text-[#555F67]">Education</Label>
                <Textarea
                  id="education"
                  value={profile.education || ""}
                  onChange={(e) => setProfile({ ...profile, education: e.target.value })}
                  placeholder="BS Computer Science, Stanford University, 2020"
                  className="min-h-[80px] border-slate-200 focus:border-[#A3C3D8]"
                  data-testid="education-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-[#555F67]">Skills</Label>
                <div className="flex gap-2">
                  <Input
                    value={skillInput}
                    onChange={(e) => setSkillInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), handleAddSkill())}
                    placeholder="Type a skill and press Enter"
                    className="border-slate-200 focus:border-[#A3C3D8]"
                    data-testid="skill-input"
                  />
                  <Button
                    type="button"
                    onClick={handleAddSkill}
                    variant="outline"
                    className="border-[#A3C3D8] text-[#4A728D]"
                  >
                    Add
                  </Button>
                </div>
                {profile.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {profile.skills.map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-[#A3C3D8]/10 text-[#4A728D] rounded-full text-sm flex items-center gap-1 cursor-pointer hover:bg-[#A3C3D8]/20"
                        onClick={() => handleRemoveSkill(skill)}
                        title="Click to remove"
                      >
                        {skill}
                        <span className="text-[#94A3B8]">×</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Save Button */}
        <div className="mt-8 flex justify-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
            data-testid="save-profile-bottom-btn"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Profile
          </Button>
        </div>
      </div>
    </div>
  );
}
